const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/**
 * PUBLIC endpoints — deliberately NOT under /api and deliberately using plain fetch, the same
 * documented exception as `interviewBookingApi.ts`: the people signing up to the Talent Community
 * are external and have no Firebase token. Signing up only creates a pending record; confirming,
 * viewing and deleting are authorised by the token in the emailed link, which the proxy checks.
 */
const BASE = `${PROXY_URL}/join`;

export type JoinLanguage = 'en' | 'de';

export interface ConsentTexts {
  intro: string;
  jobMatches: string;
  updates: string;
  ai: string;
  rights: string;
  privacy: string;
  nextStep: string;
}

export interface JoinConfig {
  /** False until legal has signed off the consent text: only @finn.com addresses may sign up. */
  live: boolean;
  consentTextVersion: string;
  texts: Record<JoinLanguage, ConsentTexts>;
  clusters: { id: string; en: string; de: string }[];
}

export interface SignupForm {
  firstName: string;
  lastName: string;
  email: string;
  linkedinUrl: string;
  cluster: string;
  via: string | null;
  language: JoinLanguage;
  jobMatches: boolean;
  updates: boolean;
  /** Honeypot. Never shown; a bot that fills it is thanked and ignored. */
  website: string;
}

export interface PersonView {
  firstName: string;
  lastName: string;
  email: string;
  linkedinUrl: string | null;
  language: JoinLanguage;
  cluster: string | null;
  consent: { status: 'pending' | 'granted'; updates: boolean; grantedAt: string | null; textVersion: string | null };
  expiresAt: string | null;
}

export class JoinError extends Error {
  status: number;
  code: string;
  details: Record<string, string>;
  constructor(status: number, code: string, details: Record<string, string> = {}) {
    super(code);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** One read of the page's config per visit; the consent text does not change mid-session. */
export const JOIN_CONFIG_QUERY = { queryKey: ['talent-join-config'], queryFn: () => fetchJoinConfig(), staleTime: Infinity, retry: 1 } as const;

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    });
  } catch {
    throw new JoinError(0, 'network');
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new JoinError(res.status, body.error || 'failed', body.details || {});
  return body as T;
}

export const fetchJoinConfig = () => call<JoinConfig>('/config');

export const submitSignup = (form: SignupForm) =>
  call<{ ok: true; emailSent?: boolean }>('/community', { method: 'POST', body: JSON.stringify(form) });

export const confirmSignup = (token: string) =>
  call<{ ok: true; manageToken: string }>(`/confirm/${encodeURIComponent(token)}`, { method: 'POST', body: '{}' });

export const fetchMyData = (token: string) => call<PersonView>(`/manage/${encodeURIComponent(token)}`);

export const setMyUpdates = (token: string, updates: boolean) =>
  call<PersonView>(`/manage/${encodeURIComponent(token)}/updates`, {
    method: 'POST',
    body: JSON.stringify({ updates }),
  });

export const deleteMyData = (token: string) =>
  call<{ ok: true }>(`/manage/${encodeURIComponent(token)}/delete`, { method: 'POST', body: '{}' });
